/**
 * Radar-pixel → neerslag-categorie. Eén bron van waarheid voor het uitlezen van de
 * KNMI-radar-PNG-frames (laag `precipitation_nowcast`, stijl `rainrate-blue-to-purple/nearest`):
 * de kaart-island doet dit client-side op het canvas, de baker doet het server-side op de
 * gedecodeerde frame-pixels voor de "Word ik nat?"-staven. Dezelfde functie ⇒ kaart en
 * staven kunnen elkaar nooit tegenspreken.
 *
 * De stijl is `nearest` ⇒ discrete kleurklassen (geen interpolatie): transparant + 5
 * blauwtinten #98d8fa→#0268c6, donkerder = zwaarder. We vatten ze samen tot 4
 * categorieën via de helderheid. bron: GetCapabilities-palet (PLTE), live geverifieerd 2026-06.
 */

/** RGBA-buffer: canvas (`Uint8ClampedArray`) óf een gedecodeerd PNG (`Uint8Array`). */
export type RgbaData = Uint8Array | Uint8ClampedArray;

/**
 * Eén radarpixel → neerslag-bucket 0=droog · 1=licht · 2=matig · 3=zwaar.
 * Transparant (a<20) ⇒ droog; daarna donkerder blauw = zwaarder.
 */
export function classifyPixel(r: number, g: number, b: number, a: number): 0 | 1 | 2 | 3 {
  if (a < 20) return 0;
  const brightness = (r + g + b) / 3;
  if (brightness < 120) return 3;
  if (brightness < 160) return 2;
  return 1;
}

/** Max neerslag-bucket in een klein blokje rond (px,py) — "regen bij jou in de buurt". */
export function sampleIntensity(
  data: RgbaData,
  width: number,
  height: number,
  px: number,
  py: number,
  radius = 3,
): 0 | 1 | 2 | 3 {
  let max: 0 | 1 | 2 | 3 = 0;
  for (let y = Math.max(0, py - radius); y <= Math.min(height - 1, py + radius); y++) {
    for (let x = Math.max(0, px - radius); x <= Math.min(width - 1, px + radius); x++) {
      const o = (y * width + x) * 4;
      const v = classifyPixel(data[o] ?? 0, data[o + 1] ?? 0, data[o + 2] ?? 0, data[o + 3] ?? 0);
      if (v > max) max = v;
    }
  }
  return max;
}
